import { createRouter, createWebHistory } from 'vue-router';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import axios from 'axios';
import i18n from './plugins/i18n';

const landingPublicRouteNames = new Set(['Landing', 'LandingPrivacy', 'LandingServices', 'LandingPartnership', 'LandingTerms', 'LandingCookie']);

// หน้าที่มีการเรียกใช้งาน LLM token (chat completion / embedding / speech-to-text / generate/score)
// → เตือน user (เฉยๆ) ทุกครั้งที่เข้าหน้าเหล่านี้ ว่าจะมีการใช้งานโทเค็นในการประมวลผล
const tokenUsageRouteNames = new Set([
  'Stream',                        // /Stream — chat playground
  'Playground',                    // /Playground — ทดสอบ/เทียบโมเดล
  'ValidationGenerate',            // /ValidationGenerate — สร้างคำถามด้วย AI
  'ValidationGenerateUpload',      // /ValidationGenerateUpload — สร้างชุดคำถามด้วย AI
  'ValidationProcess',             // /ValidationProcess — รัน + ให้คะแนนด้วย AI
  'DashboardScore',                // /DashboardScore — generate score ด้วย AI
  'UploadVector',                  // /UploadVector — ทำ embedding เอกสาร
  'VectorManagement',              // /VectorManagement — embedding จาก CSV
  'UploadSoundToDocumentDetail',   // /UploadSoundToDocumentDetail — ถอดเสียง + สรุปด้วย AI
]);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  base: import.meta.env.BASE_URL,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.name === 'Landing' && to.hash) {
      return {
        el: to.hash,
        top: 72,
        behavior: 'smooth',
      };
    }

    if (landingPublicRouteNames.has(String(to.name || ''))) {
      return {
        top: 0,
        left: 0,
      };
    }

    return undefined;
  },
  routes: [
    {
      path: '/',
      redirect: {
        name: 'Login',
      },
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/Login.vue'),
    },
    {
      path: '/RedirectFromLogin',
      name: 'RedirectFromLogin',
      component: () => import('@/views/RedirectFromLogin.vue'),
    },
    {
      path: '/LoginSnippet',
      name: 'LoginSnippet',
      component: () => import('@/views/Snippet/LoginSnippet.vue'),
    },
    {
      path: '/Snippet',
      name: 'Snippet',
      component: () => import('@/views/Snippet/Snippet.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/SnippetUnauthen',
      name: 'SnippetUnauthen',
      component: () => import('@/views/Snippet/SnippetUnauthen.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/Usermanagement',
      name: 'UserManagement',
      component: () => import('@/views/UserManagement.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/userprofile',
      name: 'UserProfile',
      component: () => import('@/views/UserProfile.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/menumanagement',
      name: 'MenuManagement',
      component: () => import('@/views/MenuManagement.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/calendar',
      name: 'Calendar',
      component: () => import('@/views/Calendar.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/permission',
      name: 'Permission',
      component: () => import('@/views/Permission.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/log',
      name: 'Log',
      component: () => import('@/views/Log.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      // หน้า "ปรับแต่ง Snippet (Chat Widget)" — เดิมชื่อ /setting ซึ่งกำกวม
      path: '/SnippetCustomization',
      name: 'SnippetCustomization',
      component: () => import('@/views/SnippetSmallCustomization.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      // backward-compat: bookmark/ลิงก์เก่าที่ชี้ /setting ยังใช้ได้
      path: '/setting',
      redirect: '/SnippetCustomization',
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      // หน้า "ปรับแต่ง Snippet เต็มจอ (Chat Widget FullV2)" — S14-P9
      path: '/SnippetFullCustomization',
      name: 'SnippetFullCustomization',
      component: () => import('@/views/SnippetFullCustomization.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/GroupReplacetext',
      name: 'GroupReplacetext',
      component: () => import('@/views/GroupReplacetext.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ReplaceText',
      name: 'ReplaceText',
      component: () => import('@/views/ReplaceText.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/FlowList',
      name: 'FlowList',
      component: () => import('@/views/FlowList.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/FlowCreator',
      name: 'FlowCreator',
      component: () => import('@/views/Flow.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    // {
    //   path: '/GroupPromptTemplate',
    //   name: 'GroupPromptTemplate',
    //   component: () => import('@/views/GroupPromptTemplate.vue'),
    //   meta: {
    //     requiresAuth: true,
    //     isProductAdmin: true,
    //   },
    // },
    {
      path: '/GPTModelManagement',
      name: 'GPTModelManagement',
      component: () => import('@/views/GPTModelManagement.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/GPTModelDetail',
      name: 'GPTModelDetail',
      component: () => import('@/views/GPTModelDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/GPTModelInternal',
      name: 'GPTModelNodeManagement',
      component: () => import('@/views/GPTModelNodeManagement.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/GPTModelInternalDetail',
      name: 'GPTModelInternalDetail',
      component: () => import('@/views/GPTModelNodeDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/VectorManagement',
      name: 'VectorManagement',
      component: () => import('@/views/UploadCSV.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/UploadVector',
      name: 'UploadVector',
      component: () => import('@/views/UploadVector.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/DataIntegration',
      name: 'DataIntegration',
      component: () => import('@/views/DataIntegration.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/DataIntegrationDetail',
      name: 'DataIntegrationDetail',
      component: () => import('@/views/DataIntegrationDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ProductGroup',
      name: 'ProductGroup',
      component: () => import('@/views/ProductGroup.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ProductDetail',
      name: 'ProductDetail',
      component: () => import('@/views/ProductDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    // Knowledge Graphs (NEXA service integration — Hybrid UI per T002 decision)
    {
      path: '/KnowledgeGraphs',
      name: 'KnowledgeGraphs',
      component: () => import('@/views/KnowledgeGraphList.vue'),
      meta: { requiresAuth: true, isProductAdmin: true },
    },
    {
      path: '/KnowledgeGraphs/:graphId',
      name: 'KnowledgeGraphDetail',
      component: () => import('@/views/KnowledgeGraphDetailEmbed.vue'),
      meta: { requiresAuth: true, isProductAdmin: true },
    },
    // เครื่องมือ pdf2md (external app ฝังผ่าน iframe, URL มาจาก config PdfToMarkdownIframeUrl)
    // แยกเป็น 2 ทางเข้า — ทางเข้าละ 1 แท็บ ไม่โชว์แท็บบาร์ให้คลิกสลับ:
    //   /PdfToMarkdownTool      = Knowledge Management → เครื่องมือ  → แท็บ "ไฟล์เอกสาร" (file)
    //   /PdfToMarkdownTestCase  = การทดสอบโมเดล → สร้างคำถามทดสอบ   → แท็บ "สร้าง test case" (qa)
    // meta.pdf2md.query ถูก append เป็น query string ให้ iframe (embed contract ฝั่งปลายทาง —
    // ดู docs/features/pdf2md-embed-tab-split-2026-08-06.md). ปลายทางที่ยังไม่ implement จะ
    // ignore query ทั้งหมด → พฤติกรรมเท่าเดิม (ไม่พัง)
    {
      path: '/PdfToMarkdownTool',
      name: 'PdfToMarkdownTool',
      component: () => import('@/views/PdfToMarkdownTool.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
        pdf2md: {
          titleKey: '_PdfToMarkdownTool',
          helpKey: 'pdf_help_toolPurpose',
          query: { embed: '1', mode: 'v1', tabs: 'file', tab: 'file' },
        },
      },
    },
    {
      path: '/PdfToMarkdownTestCase',
      name: 'PdfToMarkdownTestCase',
      component: () => import('@/views/PdfToMarkdownTool.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
        pdf2md: {
          titleKey: '_GenerateTestQuestions',
          helpKey: 'pdf_help_testCasePurpose',
          query: { embed: '1', mode: 'v1', tabs: 'qa', tab: 'qa' },
        },
      },
    },
    // {
    //   path: '/IntentGroup',
    //   name: 'IntentGroup',
    //   component: () => import('@/views/IntentGroup.vue'),
    //   meta: {
    //     requiresAuth: true,
    //     isProductAdmin: true,
    //   },
    // },
    {
      path: '/IntentSession',
      name: 'IntentSession',
      component: () => import('@/views/IntentSession.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/IntentSessionDetail',
      name: 'IntentSessionDetail',
      component: () => import('@/views/IntentSessionDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ModelAndVectorManagement',
      name: 'ModelAndVectorManagement',
      component: () => import('@/views/ModelAndVectorManagement.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/Dashboard.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/SuperAdminDashboard',
      name: 'SuperAdminDashboard',
      component: () => import('@/views/superadmin/SuperAdminDashboard.vue'),
      meta: {
        requiresAuth: true,
        isSuperAdmin: true,
      },
    },
    {
      path: '/SuperAdmin/Companies',
      redirect: '/SuperAdminDashboard',
    },
    {
      path: '/SuperAdmin/Workgroups',
      redirect: '/SuperAdminDashboard',
    },
    {
      path: '/SuperAdmin/OrganizationStructure',
      name: 'SuperAdminOrganizationStructure',
      component: () => import('@/views/superadmin/OrganizationStructure.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Users',
      name: 'SuperAdminUsers',
      component: () => import('@/views/superadmin/UserManagement.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Documents',
      name: 'SuperAdminDocuments',
      component: () => import('@/views/superadmin/DocumentManagement.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Documents/Detail',
      name: 'SuperAdminDocumentDetail',
      component: () => import('@/views/superadmin/DocumentDetail.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Sales',
      name: 'SuperAdminSales',
      component: () => import('@/views/superadmin/Sales.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/CoreConfiguration',
      name: 'SuperAdminCoreConfiguration',
      component: () => import('@/views/superadmin/CoreConfiguration.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/PersonaApiSetting',
      name: 'SuperAdminPersonaApiSetting',
      component: () => import('@/views/superadmin/PersonaApiSetting.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Subscriptions',
      name: 'SuperAdminSubscriptions',
      component: () => import('@/views/superadmin/Subscriptions.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Packages',
      name: 'SuperAdminPackages',
      component: () => import('@/views/superadmin/Packages.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Notifications',
      name: 'SuperAdminNotifications',
      component: () => import('@/views/superadmin/NotificationHistory.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/EventLogs',
      name: 'SuperAdminEventLogs',
      component: () => import('@/views/superadmin/EventLogs.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/LLMManagement',
      name: 'SuperAdminLLMManagement',
      component: () => import('@/views/superadmin/LLMManagement.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/LLMGroups',
      name: 'SuperAdminLLMGroups',
      component: () => import('@/views/superadmin/LLMGroupManagement.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/MasterPackage',
      name: 'SuperAdminMasterPackage',
      component: () => import('@/views/superadmin/MasterPackage.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/ConsentConfiguration',
      name: 'SuperAdminConsentConfiguration',
      component: () => import('@/views/superadmin/ConsentConfiguration.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/InvitationManagement',
      name: 'SuperAdminInvitationManagement',
      component: () => import('@/views/superadmin/InvitationManagement.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/ReleaseNotes',
      name: 'ReleaseNoteManagement',
      component: () => import('@/views/superadmin/ReleaseNoteManagement.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Setting',
      name: 'SuperAdminSetting',
      component: () => import('@/views/superadmin/Setting.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/Profile',
      name: 'SuperAdminProfile',
      component: () => import('@/views/superadmin/Profile.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/LandingPage',
      name: 'SuperAdminLandingPage',
      component: () => import('@/views/superadmin/LandingPage/LandingPageConfig.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/SuperAdmin/DesignSystem',
      name: 'SuperAdminDesignSystem',
      component: () => import('@/views/superadmin/DesignSystemGallery.vue'),
      meta: { requiresAuth: true, isSuperAdmin: true },
    },
    {
      path: '/DashboardScore',
      name: 'DashboardScore',
      component: () => import('@/views/DashboardScore.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationGenerate',
      name: 'ValidationGenerate',
      component: () => import('@/views/ValidationGenerate.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationGenerateUpload',
      name: 'ValidationGenerateUpload',
      component: () => import('@/views/ValidationGenerateUpload.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationProcess',
      name: 'ValidationProcess',
      component: () => import('@/views/ValidationProcess.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationPositive',
      name: 'ValidationPositive',
      component: () => import('@/views/ValidationPositive.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationNegative',
      name: 'ValidationNegative',
      component: () => import('@/views/ValidationNegative.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationPositiveDetail',
      name: 'ValidationPositiveDetail',
      component: () => import('@/views/ValidationPositiveDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ValidationNegativeDetail',
      name: 'ValidationNegativeDetail',
      component: () => import('@/views/ValidationNegativeDetail.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    // Chat Logs (LogMessage) — ซ่อนชั่วคราว: บล็อกการเข้าผ่าน link โดยตรง (redirect ไปหน้า 404)
    // หากต้องการเปิดใช้งานอีกครั้ง ให้ลบ block redirect ด้านล่าง แล้ว uncomment route เดิม
    // {
    //   path: '/LogMessage',
    //   redirect: '/error/404',
    // },
    {
      path: '/LogMessage',
      name: 'LogMessage',
      component: () => import('@/views/LogMessage.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/Stream',
      name: 'Stream',
      component: () => import('@/views/Stream.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/FullStream',
      name: 'FullStream',
      component: () => import('@/views/FullStreamReDesign.vue'),
      meta: {
        requiresAuth: false,
        isProductAdmin: true,
      },
    },
    {
      path: '/FullStreamV2',
      name: 'FullStreamV2',
      component: () => import('@/views/FullStreamReDesignV2.vue'),
      meta: {
        requiresAuth: false,
        isProductAdmin: true,
      },
    },
    {
      path: '/Landing',
      name: 'Landing',
      component: () => import('@/views/LandingPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/setup',
      name: 'Setup',
      component: () => import('@/views/Setup.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: false,
      },
    },
    {
      path: '/SetupSocial',
      name: 'SetupSocial',
      component: () => import('@/views/SetupSocial.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/EntraApps',
      name: 'EntraApps',
      component: () => import('@/views/EntraApps.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/LineEntraLinks',
      name: 'LineEntraLinks',
      component: () => import('@/views/LineEntraLinks.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/SetupGroupModel',
      name: 'SetupGroupModel',
      component: () => import('@/views/SetupGroupModel.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/modeldata',
      name: 'ModelData',
      component: () => import('@/views/ModelData.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: false,
      },
    },
    {
      path: '/ModelShortcut',
      name: 'ModelShortcut',
      component: () => import('@/views/ModelQuickGroup.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ModelQuickReply',
      name: 'ModelQuickReply',
      component: () => import('@/views/ModelQuickReply.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/SettingExternalConnector',
      name: 'SettingExternalConnector',
      component: () => import('@/views/SettingExternalConnector.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/Playground',
      name: 'Playground',
      component: () => import('@/views/PlayGround.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ServiceTheme',
      name: 'ServiceTheme',
      component: () => import('@/views/ConfigTheme.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/AccessDenied',
      name: 'AccessDenied',
      component: () => import('@/views/AccessDenied.vue'),
    },
    {
      path: '/register',
      name: 'RegisterDGADigitalID',
      component: () => import('@/views/RegisterDGADigitalID.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/register-success',
      name: 'RegisterSuccess',
      component: () => import('@/views/RegisterSuccess.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/select-company',
      name: 'SelectCompany',
      component: () => import('@/views/SelectCompany.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/no-access',
      name: 'NoAccess',
      component: () => import('@/views/NoAccess.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/InvitationManagement',
      name: 'InvitationManagement',
      component: () => import('@/views/InvitationManagement.vue'),
      meta: { requiresAuth: true, isAdmin: true, isProductAdmin: true, requiresProductAdmin: true },
    },
    {
      path: '/UserChatManagement',
      name: 'UserChatManagement',
      component: () => import('@/views/UserChatManagement.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ChatService',
      name: 'ChatService',
      component: () => import('@/views/ChatService.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ReportQuestionTop10',
      name: 'ReportQuestionTop10',
      component: () => import('@/views/ReportQuestionTop10.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ReportUserTop10',
      name: 'ReportUserTop10',
      component: () => import('@/views/ReportUserTop10.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    // {
    //   path: '/ReportPageToken',
    //   name: 'ReportPageToken',
    //   component: () => import('@/views/ReportPageToken.vue'),
    //   meta: {
    //     requiresAuth: true,
    //     isAdmin: true,
    //     isProductAdmin: true,
    //   },
    // },
    {
      path: '/ReportUserUse',
      name: 'ReportUserUse',
      component: () => import('@/views/ReportUserUse.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/PromptTemplate',
      name: 'PromptTemplate',
      component: () => import('@/views/PromptTemplate.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/Release-notes',
      name: 'Release-notes',
      component: () => import('@/views/Release-notes.vue'),
      meta: {
        requiresAuth: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ReportTokenSum',
      name: 'ReportTokenSum',
      component: () => import('@/views/ReportTokenSum.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/LogEvent',
      name: 'LogEvent',
      component: () => import('@/views/LogEvent.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/LogAPIExternal',
      name: 'LogAPIExternal',
      component: () => import('@/views/LogAPIExternal.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/GroupUserManagement',
      name: 'GroupUserManagement',
      component: () => import('@/views/GroupUserManagement.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/UserGroupDetail',
      name: 'UserGroupDetail',
      component: () => import('@/views/UserGroupDetail.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/SetupUserGroup',
      name: 'SetupUserGroup',
      component: () => import('@/views/SetupUserGroup.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    // {
    //   path: '/UploadSoundToDocument',
    //   name: 'UploadSoundToDocument',
    //   component: () => import('@/views/UploadSoundToDocument.vue'),
    //   meta: {
    //     requiresAuth: true,
    //     isAdmin: false,
    //     isProductAdmin: true,
    //   },
    // },
    {
      path: '/UploadSoundToDocumentDetail',
      name: 'UploadSoundToDocumentDetail',
      component: () => import('@/views/UploadSoundToDocumentDetail.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: false,
        isProductAdmin: true,
      },
    },
    {
      path: '/BlacklistChatUserManagement',
      name: 'BlacklistChatUserManagement',
      component: () => import('@/views/BlacklistChatUserManagement.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ReportFeedback',
      name: 'ReportFeedback',
      component: () => import('@/views/ReportFeedback.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ConsentConfig',
      name: 'ConsentConfig',
      component: () => import('@/views/ConsentConfig.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: false,
      },
    },
    {
      path: '/Consent',
      name: 'Consent',
      component: () => import('@/views/Consent.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    // เปิดไฟล์จากลิงก์ในคำตอบแชท (pdf/รูป/markdown บน NEXA) แบบเต็มแท็บ — ใช้เมื่อ browser
    // ไม่มี PDF viewer ในตัว (MarkedMarkdown.onFileLinkClick); ไม่ต้อง login เพราะ Asset proxy ตรวจสิทธิ์เอง
    {
      path: '/FilePreview',
      name: 'FilePreview',
      component: () => import('@/views/FilePreview.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/error/:code',
      name: 'error',
      component: () => import('@/views/ErrorPage.vue'),
      meta: {
        requiresAuth: false,
        isProductAdmin: true,
      },
    },
    {
      path: '/Landing/Privacy',
      name: 'LandingPrivacy',
      component: () => import('@/views/LandingPrivacyPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/Landing/Services',
      name: 'LandingServices',
      component: () => import('@/views/LandingServicesPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/Landing/Partnership',
      name: 'LandingPartnership',
      component: () => import('@/views/LandingPartnershipPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/Landing/Terms',
      name: 'LandingTerms',
      component: () => import('@/views/LandingTermsPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/Landing/Cookie',
      name: 'LandingCookie',
      component: () => import('@/views/LandingCookiePage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/maintenance',
      name: 'Maintenance',
      component: () => import('@/views/MaintenancePage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/error/404',
    },
    {
      path: '/FileManagements',
      name: 'FileManagements',
      component: () => import('@/views/FileManagement.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/ShortcutMenu',
      name: 'ShortcutMenu',
      component: () => import('@/views/ShortcutMenu.vue'),
      meta: {
        requiresAuth: true,
        isAdmin: true,
        isProductAdmin: true,
      },
    },
    {
      path: '/DesignSystem',
      name: 'PortalDesignSystem',
      component: () => import('@/views/PortalDesignSystemGallery.vue'),
      meta: {
        requiresAuth: true,
      },
    },
  ],
});

function getCurrentRoleId() {
  // อ่าน cookie ก่อน sessionStorage: cookie "roleId" ถูก set ใหม่ทุกครั้งที่ login สำเร็จ
  // (ทั้ง username/password ใน Login.vue และ SSO/DGA/ThaID ที่ backend ออกผ่าน IssueSessionCookies)
  // ขณะที่ sessionStorage อาจค้างจาก session admin ก่อนหน้าในแท็บเดียวกัน (SSO ไม่ล้าง sessionStorage)
  // ทำให้ chat user (roleId=4) ที่ login ผ่าน SSO ถูกมองเป็น admin ผิด ๆ ถ้าอ่าน sessionStorage ก่อน
  const rawRoleId =
    (Cookies.get('roleId') ?? '') === '' ? sessionStorage.getItem('roleId') : Cookies.get('roleId');
  if (rawRoleId === null || rawRoleId === undefined || rawRoleId === '') return null;
  const roleId = Number(rawRoleId);
  return Number.isNaN(roleId) ? null : roleId;
}

function getCurrentIsUserChat() {
  // cookie ก่อน sessionStorage (เหตุผลเดียวกับ getCurrentRoleId)
  const raw =
    (Cookies.get('isUserChat') ?? '') === '' ? sessionStorage.getItem('isUserChat') : Cookies.get('isUserChat');
  return raw === 'true';
}

function hasCurrentCompanyContext() {
  const rawCompanyId = Cookies.get('companyId') ?? Cookies.get('CompanyId') ?? '';
  const companyId = Number(rawCompanyId);
  const companyName = Cookies.get('companyName') ?? Cookies.get('CompanyName') ?? '';

  return Number.isFinite(companyId) && companyId > 0 && companyName.trim() !== '';
}

// Redirect a user whose client-side auth state is INCOMPLETE/broken (e.g. 'authenticated' hint
// present but company context missing) back to the login form. Always drop the stale 'authenticated'
// hint first: otherwise Login.vue's mounted() sees it and pushes back into the portal, and the guard
// bounces it here again → redirect loop → blank skeleton page. Use this instead of next('/login')
// for any "session incomplete" case (NOT for genuine permission denials — those stay on AccessDenied).
function redirectToLogin(next) {
  Cookies.remove('authenticated');
  return next({ name: 'Login' });
}

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (!requiresAuth) {
    return next();
  }

  if (!Cookies.get('authenticated')) {
    return next('/login');
  }

  const roleId = getCurrentRoleId();
  const isUserChat = getCurrentIsUserChat();
  const isSuperAdmin =
    sessionStorage.getItem('isSuperAdmin') === 'true' || Cookies.get('isSuperAdmin') === 'true';

  // chat user จริง (roleId=4 และ isUserChat=true) เท่านั้น — ห้ามเข้า portal
  // ตรวจก่อนทุก check อื่น เพื่อให้ชนะแม้ค่า isSuperAdmin/route meta จะกำกวม
  // (ทุก route ที่มาถึงตรงนี้คือ requiresAuth=true = หน้า portal; หน้า chat เป็น requiresAuth=false)
  // เด้งไป /login (same-origin, zero-config): Login.vue ไม่ล้าง cookie ในเคส authenticated+roleId=4
  // และไม่เรียก logout API → session snippet (access_token HttpOnly) ไม่หลุด
  if (roleId === 4 && isUserChat === true) {
    return next('/login');
  }

  // Routes that explicitly depend on ProductAdmin feature must be blocked
  // when the environment does not enable that feature.
  const requiresProductAdmin = to.matched.some((record) => record.meta.requiresProductAdmin);
  const isProductAdminEnabled = import.meta.env.VITE_APP_ProductAdmin === 'true' || import.meta.env.VITE_APP_ProductAdmin === true;
  if (requiresProductAdmin && !isProductAdminEnabled) {
    return next({ name: 'AccessDenied' });
  }

  // SuperAdmin-only routes: block non-superadmin users
  // isSuperAdmin ตรวจจาก sessionStorage เท่านั้น — roleId=0 เป็น ChatFramework SuperAdmin ซึ่งต่างจาก ProductAdmin
  // fallback อ่านจาก cookie "isSuperAdmin" สำหรับ DGA/SSO flow ที่ไม่ผ่าน Login.vue (server-side redirect)
  const isSuperAdminRoute = to.matched.some((record) => record.meta.isSuperAdmin);
  if (isSuperAdminRoute) {
    if (!isSuperAdmin) return next({ name: 'AccessDenied' });
    return next();
  }

  // หน้า portal ปกติทุกหน้าต้องอยู่ใน context ของบริษัทแล้วเท่านั้น
  // SuperAdmin ที่ยังไม่ได้ SwitchCompany ให้กลับไปหน้า ProductAdmin แทนการเข้า layer บริษัท
  if (!hasCurrentCompanyContext()) {
    if (isSuperAdmin) return next({ name: 'SuperAdminDashboard' });
    // Auth state incomplete (authenticated hint present but no company context) → treat as logged-out.
    return redirectToLogin(next);
  }

  // admin-only routes (meta.isAdmin) → เฉพาะ 0/1/6 หรือ superadmin
  // route อื่น (เช่น GPTModelManagement) → ผู้ใช้ที่ login แล้วและไม่ใช่ chat user เข้าได้ (รวม roleId=2)
  const isAdminRoute = to.matched.some((record) => record.meta.isAdmin);
  if (!isAdminRoute) {
    return next();
  }
  const isAllowedRole = roleId === 0 || roleId === 1 || roleId === 6 || isSuperAdmin;
  if (isAllowedRole) {
    return next();
  }

  return next({ name: 'AccessDenied' });
});

// อ่านค่า config เปิด/ปิด popup โทเค็น (ตั้งที่ /SuperAdmin/CoreConfiguration) แบบ cache ครั้งเดียวต่อการโหลดแอป
// เรียกผ่าน axios ตรง ไม่ใช้ $call เพราะ call.js import router → เลี่ยง circular import
let _tokenPopupEnabledPromise = null;
function isTokenPopupEnabled() {
  if (!_tokenPopupEnabledPromise) {
    _tokenPopupEnabledPromise = axios
      .get(`${import.meta.env.VITE_APP_API}api/v0/Chat/GetTokenUsageWarningEnabled`, { withCredentials: true })
      .then((res) => {
        const v = res?.data?.data;
        return v === '1' || v === 1 || v === true; // โชว์เฉพาะเมื่อเปิดชัดเจน; ไม่เจอ/ค่าว่าง = ไม่โชว์
      })
      .catch(() => false); // error / ไม่เจอ setting → default ไม่โชว์
  }
  return _tokenPopupEnabledPromise;
}

// แจ้งเตือน (แบบ info เฉยๆ ไม่บล็อกการใช้งาน) เมื่อ "เข้า" หน้าที่มีการใช้งาน LLM token
// ใช้ afterEach เพื่อให้ navigate เสร็จก่อนแล้วค่อยเด้ง popup — ถ้า guard redirect ไปที่อื่น จะไม่เด้งผิดหน้า
router.afterEach(async (to, from) => {
  const toName = String(to.name || '');
  // เด้งเฉพาะตอนเข้าหน้าใหม่ — ไม่เด้งซ้ำเมื่อเปลี่ยนแค่ param ภายในหน้าเดียวกัน
  if (String(from.name || '') === toName) return;


  // เคารพค่า config เปิด/ปิด (ตั้งโดย SuperAdmin) — await ก่อนโชว์ เพื่อไม่ให้เด้งตอนถูกปิดไว้
  if (!(await isTokenPopupEnabled())) return;

  Swal.fire({
    icon: 'info',
    title: i18n.global.t('tokenUsageWarningTitle'),
    text: i18n.global.t('tokenUsageWarningText'),
    confirmButtonText: i18n.global.t('tokenUsageWarningConfirm'),
    confirmButtonColor: '#3085d6',
  });
});

export default router;
